import { useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import { useEntitlements } from '../subscription/offers';
import { useTrial } from '../subscription/trial';
import { useT } from '../i18n/i18n';
import Reversi, { REVERSI_DEFAULTS, ReversiOptionsForm, type ReversiOptions } from './Reversi';
import CarPark, { CAR_PARK_DEFAULTS, CarParkOptionsForm, type CarParkOptions } from './CarPark';
import WordTiles, { WORD_TILES_DEFAULTS, WordTilesOptionsForm, wordsFor, type WordTilesOptions } from './WordTiles';
import './leisure.css';

type GameDef = { id: string; name: string; blurb: string; icon: 'reversi' | 'words' | 'cars' };

const GAMES: GameDef[] = [
  { id: 'reversi', name: 'Reversi', blurb: 'Two players take turns. Place a piece so it closes a line of the other colour, and those pieces turn to yours.', icon: 'reversi' },
  { id: 'word-tiles', name: 'Word Tiles', blurb: 'Drag letters onto the board to make words that join up, like a crossword.', icon: 'words' },
  { id: 'car-park', name: 'Car Park', blurb: 'The car park is full. Tap a car to drive it out. A car can only leave when nothing is in front of it.', icon: 'cars' },
];

function GameIcon({ icon }: { icon: GameDef['icon'] }) {
  return (
    <span className={`lz-icon is-${icon}`} aria-hidden="true">
      <svg viewBox="0 0 40 40">
        {icon === 'reversi' && (
          <>
            <circle cx="15" cy="16" r="10" fill="#262a36" stroke="#ffffff" strokeOpacity="0.3" />
            <circle cx="25" cy="25" r="10" fill="#f4f1ea" />
          </>
        )}
        {icon === 'words' && (
          <>
            <rect x="4" y="9" width="15" height="15" rx="4" fill="#f6c9a0" />
            <rect x="21" y="16" width="15" height="15" rx="4" fill="#bfe4b4" />
            <text x="11.5" y="21" fontSize="11" fontWeight="800" textAnchor="middle" fill="#22263a">A</text>
            <text x="28.5" y="28" fontSize="11" fontWeight="800" textAnchor="middle" fill="#22263a">B</text>
          </>
        )}
        {icon === 'cars' && (
          <>
            <rect x="11" y="3" width="18" height="34" rx="6" fill="#ffffff" />
            <path d="M13.5 12 Q20 9.5 26.5 12 L25.5 16 Q20 15 14.5 16 Z" fill="#2c323d" />
            <rect x="14.5" y="18" width="11" height="10" rx="3" fill="#f3d7cf" />
            <rect x="13" y="4.5" width="4" height="2" rx="1" fill="#e9b949" />
            <rect x="23" y="4.5" width="4" height="2" rx="1" fill="#e9b949" />
          </>
        )}
      </svg>
    </span>
  );
}

function useCanPlay(): boolean | null {
  const { subscriptionStatus } = useAuth();
  const entitlements = useEntitlements();
  const { trial } = useTrial();
  if (entitlements === null) return null;
  const paid = subscriptionStatus === 'ACTIVE' || subscriptionStatus === 'TRIALING';
  return paid && entitlements.main && !trial?.limited;
}

function Locked() {
  const t = useT();
  return (
    <div className="card lz-locked" role="note">
      <p>{t('Games can be played with a subscription or during the full trial.')}</p>
      <div className="toolbar">
        <Link className="btn" to="/account/subscription">{t('See plans')}</Link>
      </div>
    </div>
  );
}

function GamesGate({ children }: { children: ReactNode }) {
  const { settings } = useSettings();
  if (!settings.features.games) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function GamesList() {
  const t = useT();
  const canPlay = useCanPlay();
  return (
    <GamesGate>
      <section className="lz-page">
        <h1>{t('Games')}</h1>
        <p className="field-hint">{t('Games to play for free time or as a reward. Choose one to see its options.')}</p>
        {canPlay === false && <Locked />}
        <ul className="lz-list">
          {GAMES.map((g) => (
            <li key={g.id}>
              <Link className="card lz-game-card" to={`/games/${g.id}`}>
                <GameIcon icon={g.icon} />
                <span>
                  <strong>{t(g.name)}</strong>
                  <span className="field-hint">{t(g.blurb)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </GamesGate>
  );
}

export function GamePlay() {
  const t = useT();
  const { id } = useParams();
  const canPlay = useCanPlay();
  const game = GAMES.find((g) => g.id === id);
  const [reversi, setReversi] = useState<ReversiOptions>(REVERSI_DEFAULTS);
  const [words, setWords] = useState<WordTilesOptions>(WORD_TILES_DEFAULTS);
  const [cars, setCars] = useState<CarParkOptions>(CAR_PARK_DEFAULTS);
  const [playing, setPlaying] = useState(0);

  if (!game) return <Navigate to="/games" replace />;
  const blocked = game.id === 'word-tiles' && wordsFor(words).length === 0;

  return (
    <GamesGate>
      <section className="lz-page">
        <p><Link to="/games">{t('All games')}</Link></p>
        <h1>{t(game.name)}</h1>
        {playing > 0 && canPlay ? (
          <>
            {game.id === 'reversi' && <Reversi key={playing} options={reversi} />}
            {game.id === 'word-tiles' && <WordTiles key={playing} options={words} onAgain={() => setPlaying((k) => k + 1)} />}
            {game.id === 'car-park' && <CarPark key={playing} options={cars} onAgain={() => setPlaying((k) => k + 1)} />}
            <div className="toolbar lz-actions">
              <button type="button" className="btn secondary" onClick={() => setPlaying(0)}>{t('Change options')}</button>
            </div>
          </>
        ) : (
          <>
            <p className="field-hint">{t(game.blurb)}</p>
            {canPlay === false && <Locked />}
            <form
              className="card lz-options"
              onSubmit={(e) => { e.preventDefault(); if (canPlay && !blocked) setPlaying((n) => n + 1); }}
            >
              <fieldset disabled={!canPlay}>
                <legend className="field-label">{t('Options')}</legend>
                {game.id === 'reversi' && <ReversiOptionsForm value={reversi} onChange={setReversi} />}
                {game.id === 'word-tiles' && <WordTilesOptionsForm value={words} onChange={setWords} />}
                {game.id === 'car-park' && <CarParkOptionsForm value={cars} onChange={setCars} />}
              </fieldset>
              <div className="toolbar">
                <button type="submit" className="btn" disabled={!canPlay || blocked}>{t('Continue')}</button>
              </div>
            </form>
          </>
        )}
      </section>
    </GamesGate>
  );
}
